/**
 * Estimated / Asserted / Confirmed for Vehicle Cost Rates.
 *
 * Maps onto Truth Layer provenance (Constitution §13.2) for the first two:
 * - Estimated = built-in default / standard model rate
 * - Asserted  = user-entered value that differs from the estimate
 * Confirmed is a review stamp on an Asserted value (not a provenance class) —
 * green when live value still matches rate_confirmations, and always labelled
 * "Asserted · confirmed" so it maps onto Asserted rather than replacing it.
 */

const FLOAT_EPS = 1e-9;

export type FleetCostValueStatus = 'estimated' | 'asserted' | 'confirmed';

/** Persisted confirmed values keyed by stable field paths. */
export type FleetCostRateConfirmations = Record<string, number>;

export function valuesNearlyEqual(
  a: number | null | undefined,
  b: number | null | undefined,
): boolean {
  if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= FLOAT_EPS;
}

/** True when `current` differs from the built-in estimate (or no estimate exists for a custom row). */
export function isFleetCostValueOverridden(
  current: number | null | undefined,
  defaultValue: number | null | undefined,
): boolean {
  if (defaultValue == null || !Number.isFinite(defaultValue)) {
    return current != null && Number.isFinite(current);
  }
  if (current == null || !Number.isFinite(current)) return true;
  return Math.abs(current - defaultValue) > FLOAT_EPS;
}

export function resolveFleetCostValueStatus(
  current: number | null | undefined,
  defaultValue: number | null | undefined,
  confirmedValue: number | null | undefined,
): FleetCostValueStatus {
  if (!isFleetCostValueOverridden(current, defaultValue)) return 'estimated';
  if (valuesNearlyEqual(current, confirmedValue)) return 'confirmed';
  return 'asserted';
}

export function fleetCostConfirmKeyVehicle(
  table: 'tonnage' | 'emergency',
  vehicleName: string,
  field: string,
): string {
  return `v.${table}.${vehicleName}.${field}`;
}

export function fleetCostConfirmKeyTrailer(field: string): string {
  return `trailer.${field}`;
}

export function fleetCostConfirmKeyRunning(
  map: string,
  className: string,
  sub?: string,
): string {
  return sub ? `r.${map}.${className}.${sub}` : `r.${map}.${className}`;
}

/** Read-mode cell text by status. */
export function fleetCostValueTextClass(status: FleetCostValueStatus): string {
  if (status === 'confirmed') return 'text-success font-semibold';
  if (status === 'asserted') return 'text-ring font-semibold';
  return 'text-warning/90 font-normal';
}

/** Edit-mode input chrome (thicker border while editing). */
export function fleetCostValueInputClass(
  status: FleetCostValueStatus,
  baseInputClass: string,
): string {
  if (status === 'confirmed') {
    return `${baseInputClass} border-success/70 text-success`;
  }
  if (status === 'asserted') {
    return `${baseInputClass} border-ring/70 text-ring`;
  }
  return `${baseInputClass} border-warning/60 text-warning`;
}

export function fleetCostValueTitle(
  status: FleetCostValueStatus,
  defaultValue: number | null | undefined,
  formatDefault?: (n: number) => string,
): string | undefined {
  if (status === 'estimated') return 'Estimated value';
  const estimatedNote =
    defaultValue != null && Number.isFinite(defaultValue)
      ? formatDefault
        ? formatDefault(defaultValue)
        : String(defaultValue)
      : null;
  // Confirmed is a review attribute on an Asserted value, not its own
  // provenance class (Constitution §13.2) — always label it as Asserted.
  if (status === 'confirmed') {
    return estimatedNote
      ? `Asserted · confirmed (estimated ${estimatedNote})`
      : 'Asserted · confirmed';
  }
  if (estimatedNote) return `Asserted (estimated ${estimatedNote})`;
  return 'Asserted (no matching estimated class)';
}

/** @deprecated Prefer fleetCostValueTitle — kept for call-site migration. */
export function fleetCostOverrideTitle(
  overridden: boolean,
  defaultValue: number | null | undefined,
  formatDefault?: (n: number) => string,
): string | undefined {
  return fleetCostValueTitle(
    overridden ? 'asserted' : 'estimated',
    defaultValue,
    formatDefault,
  );
}

/**
 * Stamp every asserted rate on a tab as confirmed; drop prior stamps for keys
 * this tab owns that are no longer asserted (back to estimate).
 */
export function mergeTabConfirmations(args: {
  existing: FleetCostRateConfirmations | undefined;
  /** Live asserted values to stamp as confirmed (key → value). */
  overridesToConfirm: FleetCostRateConfirmations;
  /** Keys owned by this tab — dropped from existing before re-stamping. */
  retainKey: (key: string) => boolean;
}): FleetCostRateConfirmations {
  const next: FleetCostRateConfirmations = {};
  for (const [key, value] of Object.entries(args.existing ?? {})) {
    if (!args.retainKey(key)) {
      next[key] = value;
    }
  }
  for (const [key, value] of Object.entries(args.overridesToConfirm)) {
    if (Number.isFinite(value)) next[key] = value;
  }
  return next;
}
